import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import Layout from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Users, TrendingUp, RefreshCw, Trophy } from "lucide-react";

/**
 * AdminReferrals – Owner/admin only.
 * Viser leaderboard over hvem som har vervet flest nye brukere via sin referral-kode
 * (registrering med ?ref=KODE eller via skaper-handle). Hentes fra
 * GET /api/referrals/admin/leaderboard.
 */
const WINDOWS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
  { value: 365, label: "1y" },
];

export default function AdminReferrals({ embedded = false }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "owner" || me?.role === "admin";
  const Wrap = embedded ? React.Fragment : Layout;

  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (windowDays = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/referrals/admin/leaderboard?days=${windowDays}&limit=100`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Kunne ikke laste leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, days]);

  if (!isAdmin) {
    return (
      <Wrap>
        <div className="text-center text-[var(--cream-soft)] py-20">
          Du må være admin for å se denne siden.
        </div>
      </Wrap>
    );
  }

  const totalReferred = rows.reduce((sum, r) => sum + (r.count || 0), 0);
  const totalPaying = rows.reduce((sum, r) => sum + (r.paying || 0), 0);
  const conversion = totalReferred > 0 ? Math.round((totalPaying / totalReferred) * 100) : 0;

  return (
    <Wrap>
      <div className="max-w-6xl mx-auto" data-testid="admin-referrals-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-1 flex items-center gap-2">
              <Trophy className="h-3 w-3" /> ADMIN · VERVING
            </div>
            <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Referral-leaderboard</h1>
            <p className="text-sm text-[var(--cream-soft)] mt-2 max-w-2xl">
              Hvem driver trafikk? Topp-listen viser skapere/brukere hvis kode (eller skaper-handle)
              ble brukt ved registrering, og hvor mange av de vervede som har blitt betalende kunder
              (VIP eller har kjøpt chips).
            </p>
          </div>
          <button
            onClick={() => load(days)}
            disabled={loading}
            className="px-4 py-2 text-xs font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)] disabled:opacity-40"
            data-testid="referrals-refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 inline mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "LASTER…" : "OPPDATER"}
          </button>
        </div>

        {/* Vindu-velger */}
        <div className="flex items-center gap-2 mb-6" data-testid="referrals-window-picker">
          <span className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)]">PERIODE:</span>
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={`px-3 py-1 text-xs font-meta tracking-[0.15em] border ${
                days === w.value
                  ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                  : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)] hover:text-[var(--cream)]"
              }`}
              data-testid={`referrals-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {/* KPI-kort */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="panel p-4" data-testid="kpi-total-referrers">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
              <Users className="h-3 w-3 inline mr-1" /> AKTIVE VERVERE
            </div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)]">{rows.length}</div>
          </div>
          <div className="panel p-4" data-testid="kpi-total-referred">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
              NYE BRUKERE
            </div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)]">{totalReferred}</div>
          </div>
          <div className="panel p-4" data-testid="kpi-conversion">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
              <TrendingUp className="h-3 w-3 inline mr-1" /> KONVERTERING
            </div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)]">
              {conversion}%
              <span className="font-manrope text-xs text-[var(--cream-faint)] ml-2 normal-case not-italic">
                ({totalPaying} betalende)
              </span>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="panel p-0 overflow-hidden" data-testid="referrals-leaderboard">
          {loading ? (
            <div className="text-center text-[var(--cream-soft)] py-12">Laster leaderboard…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-[var(--cream-faint)] py-12 text-sm">
              Ingen verving registrert i denne perioden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--noir)] border-b border-white/10">
                  <tr className="text-left text-[var(--cream-soft)] uppercase text-[10px] tracking-[0.15em] font-meta">
                    <th className="px-4 py-3 w-12">#</th>
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Eier</th>
                    <th className="px-4 py-3 text-right">Vervet</th>
                    <th className="px-4 py-3 text-right">Betalende</th>
                    <th className="px-4 py-3 text-right">Konvertering</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const conv = r.count > 0 ? Math.round((r.paying / r.count) * 100) : 0;
                    const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                    return (
                      <tr
                        key={`${r.code}-${i}`}
                        className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                        data-testid={`referral-row-${r.code}`}
                      >
                        <td className="px-4 py-3 text-[var(--cream-faint)] font-mono">
                          {medal ? <span className="text-base">{medal}</span> : i + 1}
                        </td>
                        <td className="px-4 py-3 font-mono text-[var(--magenta)]">{r.code}</td>
                        <td className="px-4 py-3">
                          {r.owner_username ? (
                            <Link
                              to={`/profile/${r.owner_username}`}
                              className="text-[var(--cream)] hover:text-[var(--magenta)]"
                              data-testid={`referral-owner-${r.owner_username}`}
                            >
                              @{r.owner_username}
                              {r.owner_creator_handle && r.owner_creator_handle !== r.owner_username && (
                                <span className="text-[10px] text-[var(--cream-faint)] ml-2">
                                  · skaper: {r.owner_creator_handle}
                                </span>
                              )}
                            </Link>
                          ) : (
                            <span className="text-[var(--cream-faint)] text-xs">— ukjent / slettet</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--cream)] font-fraunces italic text-lg">
                          {r.count}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--gold)] font-mono">
                          {r.paying}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className={`font-mono text-xs ${
                              conv >= 30
                                ? "text-emerald-400"
                                : conv >= 10
                                ? "text-amber-400"
                                : "text-[var(--cream-faint)]"
                            }`}
                          >
                            {conv}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] text-[var(--cream-faint)] mt-4 leading-relaxed max-w-2xl">
          Vindu = registrerte i siste {days} dager. "Betalende" = bruker har tier=VIP eller chip-saldo {">"} 0.
          Koder matches på <code className="text-[var(--magenta)]">username</code> og{" "}
          <code className="text-[var(--magenta)]">creator_handle</code> (UPPERCASE).
        </p>
      </div>
    </Wrap>
  );
}
